const toCoordinate = (value?: number | string | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
};

const URL_PATTERN = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCTUATION = new Set(['.', ',', '!', '?', ';', ':']);
const CLOSING_BRACKETS: Record<string, string> = {
  ')': '(',
  ']': '[',
  '}': '{',
};

export const normalizeExternalUrl = (value: string) => (
  value.toLowerCase().startsWith('www.') ? `https://${value}` : value
);

const trimTrailingPunctuation = (value: string) => {
  let endIndex = value.length;

  while (endIndex > 0) {
    const lastCharacter = value[endIndex - 1];

    if (TRAILING_PUNCTUATION.has(lastCharacter)) {
      endIndex -= 1;
      continue;
    }

    const openingBracket = CLOSING_BRACKETS[lastCharacter];

    if (!openingBracket) {
      break;
    }

    const candidate = value.slice(0, endIndex - 1);
    const openingCount = Array.from(candidate).filter((character) => character === openingBracket).length;
    const closingCount = Array.from(candidate).filter((character) => character === lastCharacter).length;

    if (closingCount >= openingCount) {
      endIndex -= 1;
      continue;
    }

    break;
  }

  return value.slice(0, endIndex);
};

export const isKomootUrl = (value: string) => {
  try {
    const normalizedUrl = new URL(normalizeExternalUrl(value));
    const labels = normalizedUrl.hostname.toLowerCase().split('.').filter(Boolean);
    const komootIndex = labels.lastIndexOf('komoot');
    const suffixLength = labels.length - komootIndex - 1;

    return komootIndex >= 0 && suffixLength >= 1 && suffixLength <= 2;
  } catch {
    return false;
  }
};

export const extractKomootUrls = (text?: string | null) => {
  if (!text) {
    return [];
  }

  const urls: string[] = [];

  text.replace(URL_PATTERN, (match) => {
    const normalizedUrl = normalizeExternalUrl(trimTrailingPunctuation(match));

    if (normalizedUrl && isKomootUrl(normalizedUrl) && !urls.includes(normalizedUrl)) {
      urls.push(normalizedUrl);
    }

    return match;
  });

  return urls;
};

export const buildGoogleMapsUrl = (
  latitude?: number | string | null,
  longitude?: number | string | null,
  query?: string | null,
) => {
  const parsedLatitude = toCoordinate(latitude);
  const parsedLongitude = toCoordinate(longitude);

  if (parsedLatitude !== null && parsedLongitude !== null) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${parsedLatitude},${parsedLongitude}`)}`;
  }

  if (query?.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query.trim())}`;
  }

  return null;
};